import {AxiosError, AxiosResponse} from 'axios';
import type {ComponentPublicInstance} from 'vue';
import {useErrorReportStore} from '@core/store/ErrorReportStore';
import {useUserStore} from '@core/store/UserStore';
import {useEnvironmentStore} from '@internalDevportal/store/EnvironmentStore';
import {getErrorMessage} from '@core/utils/getErrorMessage';

// Interface to represent the structure of error objects handled in this service
// Instead of using the AxiosError type directly, we define our own interface
export interface ErrorResponse extends AxiosError {
  response?: AxiosResponse & {
    data?: {
      error?: {
        message?: string | {message?: string};
        details?:
          | unknown[]
          | {
              messages?: {
                name?: string[];
              };
            };
      };
    };
  };
}

export type ErrorType =
  | 'HttpError'
  | 'VueError'
  | 'WindowError'
  | 'UnhandledRejectionError';

function getErrorReportContext() {
  return {
    errorReportStore: useErrorReportStore(),
    userStore: useUserStore(),
    environmentStore: useEnvironmentStore(),
  };
}

function formatVueErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return String(error);
}

function withErrorPrefix(text: string): string {
  return `Error: ${text}`;
}

class ErrorHandler {
  handleHttpError(error: AxiosError) {
    const {errorReportStore, userStore, environmentStore} =
      getErrorReportContext();

    errorReportStore.addError({
      message:
        error && (error as Error).message
          ? (error as Error).message
          : JSON.stringify(error),
      type: 'HttpError',
      component: error.config?.url,
      email: userStore.user?.profile?.email,
      errorCode: error.response?.status?.toString() ?? 'unknown',
      environmentId: environmentStore.currentEnvironment?.id,
    });
  }

  handleVueError(
    error: unknown,
    instance: ComponentPublicInstance | null,
    info: string,
  ) {
    const {errorReportStore, userStore, environmentStore} =
      getErrorReportContext();

    console.error('Vue error:', error);
    console.error('Vue instance:', instance);
    console.error('Vue info:', info);

    const message = formatVueErrorMessage(error);

    errorReportStore.addError({
      message,
      type: 'VueError',
      component: instance?.$options?.__name,
      email: userStore.user?.profile?.email,
      errorCode: info,
      environmentId: environmentStore.currentEnvironment?.id,
    });
  }

  handleWindowError(event: ErrorEvent) {
    console.error('Window error:', event);
    const {errorReportStore, userStore, environmentStore} =
      getErrorReportContext();

    errorReportStore.addError({
      message: event.message,
      type: 'WindowError',
      component: event.filename,
      email: userStore.user?.profile?.email,
      errorCode: `${event.lineno}:${event.colno}`,
      environmentId: environmentStore.currentEnvironment?.id,
    });
  }

  handleUnhandledRejection(event: PromiseRejectionEvent) {
    console.error('Unhandled promise rejection:', event);
    const {errorReportStore, userStore, environmentStore} =
      getErrorReportContext();

    let message: string;
    let errorCode: string;
    if (event.reason instanceof Error) {
      message = event.reason.message;
      errorCode = event.reason.name;
    } else {
      message = String(event.reason);
      errorCode = 'UnhandledRejection';
    }

    errorReportStore.addError({
      message,
      type: 'UnhandledRejectionError',
      component: 'Global',
      email: userStore.user?.profile?.email,
      errorCode,
      environmentId: environmentStore.currentEnvironment?.id,
    });
  }

  constructHttpErrorSnackbarMessage(
    axiosError: AxiosError,
    t: (key: string) => string,
  ): string {
    if (!axiosError.response) {
      return t('internalDevPortal.error500');
    }

    if (axiosError.response.status === 409) {
      return `Conflict: ${t('core.conflictError')}`;
    }

    const message = getErrorMessage(axiosError);
    if (message && message !== 'An unexpected error occurred') {
      return withErrorPrefix(message);
    }

    if (axiosError.response.status >= 500) {
      return t('error500');
    }

    return t('internalDevPortal.error500');
  }
}

export default new ErrorHandler();
